import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectIsAdmin } from '../../features/auth/authSlice';
import { LoadingButton } from '../ui/LoadingSpinner';
import OmTextField from '../ui/OmTextField';
import OmSelect from '../ui/OmSelect';
import OmSelectOption from '../ui/OmSelectOption';
import OmButton from '../ui/OmButton';

function UserForm({ user, onSubmit, isLoading, onCancel }) {
  const isAdmin = useSelector(selectIsAdmin);
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    roles: [],
    is_active: true
  });

  const [errors, setErrors] = useState({});

  // Available roles from backend schema
  const availableRoles = [
    'HR',
    'Broker', 
    'CRM',
    'Claims_Officer',
    'Approver',
    'Finance',
    'Admin'
  ];

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        roles: user.userRoles?.map(ur => ur.role?.name) || [],
        is_active: user.is_active !== undefined ? user.is_active : true
      });
    }
  }, [user]);

  const handleChange = (e) => {
    // Handle both regular events and OM component events
    const target = e.target || e.detail || e;
    const name = target.name || e.target?.name;
    const value = target.value || e.target?.value || (e.detail && e.detail.value);
    const type = target.type || e.target?.type;
    const checked = target.checked || e.target?.checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear field-specific errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  // Handle role selection changes for OmSelect (single role only)
  const handleRoleSelectChange = (e) => {
    const selectedRole = e.target.value;
    if (selectedRole) {
      setFormData(prev => ({
        ...prev,
        roles: [selectedRole] // Only allow one role
      }));
    }
    
    // Clear roles error when role is selected
    if (errors.roles) {
      setErrors(prev => ({ ...prev, roles: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }



    // Roles validation
    if (formData.roles.length === 0) {
      newErrors.roles = 'At least one role must be selected';
    }

    // Admin role validation - only admins can assign admin role
    if (formData.roles.includes('Admin') && !isAdmin) {
      newErrors.roles = 'You do not have permission to assign Admin role';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log('📝 [UserForm] Form submission started');
    console.log('📝 [UserForm] Form data:', formData);
    
    if (!validateForm()) {
      console.warn('⚠️ [UserForm] Form validation failed:', errors);
      return;
    }

    // Prepare submission data
    const submitData = {
      username: formData.username.trim(),
      email: formData.email.trim(),
      roles: formData.roles,
      is_active: formData.is_active
    };

    console.log('✅ [UserForm] Form validated, submitting:', submitData);
    onSubmit(submitData);
  };

  // Only admins can access this form
  if (!isAdmin) {
    return (
      <div className="min-h-96 flex items-center justify-center bg-silver-mist">
        <div className="card max-w-md w-full mx-4" style={{ borderRadius: '0.5rem' }}>
          <div className="card-header text-center">
            <h2 className="text-xl font-display">Access Denied</h2>
          </div>
          <div className="card-body text-center">
            <div className="text-error text-base mb-4">
              You do not have permission to manage users.
            </div>
            <p className="text-steel text-sm mb-6">
              Contact your administrator for access to user management features.
            </p>
            <OmButton
              onClick={onCancel}
              buttonType="outlined-button"
              buttonSize="medium"
              leadingIcon="arrow_back"
            >
              Back to List
            </OmButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-silver-mist py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="card" style={{ borderRadius: '0.5rem' }}>
            <div className="card-header">
              <h1 className="text-2xl font-display tracking-tight">
                {user ? 'Edit User Account' : 'Create New User Account'}
              </h1>
              <p className="text-crystal-white/85 text-sm mt-1 font-normal">
                {user ? 'Update user information and permissions' : 'Configure user credentials and role assignments'}
              </p>
            </div>
          </div>
        </div>

        {/* Main Form */}
        <div className="card" style={{ borderRadius: '0.5rem' }}>
          <div className="card-body">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Account Information Section */}
              <div className="space-y-6">
                <div className="border-b border-silver-mist pb-4">
                  <h3 className="text-lg font-display text-deep-jungle mb-2">Account Information</h3>
                  <p className="text-steel text-sm">Basic user account details and login credentials</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Username Field */}
                  <div className="space-y-2">
                    <div style={{
                      '--omk-text-field-container-color': '#ffffff',
                      '--omk-text-field-label-text-color': '#006250',
                      '--omk-text-field-input-text-color': '#334155',
                      '--omk-text-field-outline-color': errors.username ? '#ef4444' : 'rgba(86, 163, 83, 0.3)',
                      '--omk-text-field-focus-outline-color': errors.username ? '#ef4444' : '#56a353',
                      '--omk-text-field-caret-color': '#56a353'
                    }}>
                      <OmTextField
                        label="Username *"
                        placeholder="Enter username"
                        value={formData.username}
                        onChange={handleChange}
                        name="username"
                        type="text"
                        required
                        customValidationMessage={errors.username}
                        alwaysShowValidationMessage={!!errors.username}
                      />
                    </div>
                  </div>

                  {/* Email Field */}
                  <div className="space-y-2">
                    <div style={{
                      '--omk-text-field-container-color': '#ffffff',
                      '--omk-text-field-label-text-color': '#006250',
                      '--omk-text-field-input-text-color': '#334155',
                      '--omk-text-field-outline-color': errors.email ? '#ef4444' : 'rgba(86, 163, 83, 0.3)',
                      '--omk-text-field-focus-outline-color': errors.email ? '#ef4444' : '#56a353',
                      '--omk-text-field-caret-color': '#56a353'
                    }}>
                      <OmTextField
                        label="Email Address *"
                        placeholder="Enter email address"
                        value={formData.email}
                        onChange={handleChange}
                        name="email"
                        type="email"
                        required
                        customValidationMessage={errors.email}
                        alwaysShowValidationMessage={!!errors.email}
                      />
                    </div>
                  </div>
                </div>

                {/* Password notification for new users */}
                {!user && (
                  <div className="bg-zesty-lime/10 border border-zesty-lime/30 p-4 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <svg className="w-5 h-5 text-zesty-lime mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-graphite">Password Setup</h4>
                        <p className="text-sm text-steel mt-1">
                          A temporary password will be automatically generated and emailed to the user. 
                          They will be required to change it upon their first login.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Permissions Section */}
              <div className="space-y-6">
                <div className="border-b border-silver-mist pb-4">
                  <h3 className="text-lg font-display text-deep-jungle mb-2">Permissions & Access</h3>
                  <p className="text-steel text-sm">Configure user roles and account status</p>
                </div>

                {/* Roles Selection */}
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-graphite">
                    User Role
                    <span className="text-error ml-1">*</span>
                    <span className="text-steel text-xs ml-2 font-normal">(select one role)</span>
                  </label>
                  
                  <div className="bg-silver-mist/50 p-6 border-l-4 border-woodland-moss rounded-lg">
                    {/* Role Selection Dropdown */}
                    <div className="relative">
                      <div style={{
                        '--omk-select-text-field-container-color': '#ffffff',
                        '--omk-select-text-field-label-text-color': '#006250',
                        '--omk-select-text-field-input-text-color': '#334155',
                        '--omk-select-text-field-outline-color': errors.roles ? '#ef4444' : 'rgba(86, 163, 83, 0.3)',
                        '--omk-select-text-field-focus-outline-color': errors.roles ? '#ef4444' : '#56a353',
                        '--omk-select-menu-container-z-index': '10'
                      }}>
                        <OmSelect
                          label="Select User Role"
                          value={formData.roles[0] || ""}
                          onChange={handleRoleSelectChange}
                          customValidationMessage={errors.roles}
                          alwaysShowValidationMessage={!!errors.roles}
                          style={{ position: 'relative', zIndex: 1 }}
                        >
                          <OmSelectOption value="">Choose a role</OmSelectOption>
                          {availableRoles.map(role => (
                            <OmSelectOption key={role} value={role}>
                              {role.replace('_', ' ')}
                            </OmSelectOption>
                          ))}
                        </OmSelect>
                      </div>
                    </div>
                  </div>
                  
                  {errors.roles && (
                    <div className="flex items-start space-x-2 mt-2">
                      <div className="flex-shrink-0 w-1 h-4 bg-error mt-0.5"></div>
                      <p className="text-sm text-error">{errors.roles}</p>
                    </div>
                  )}
                </div>

                {/* Account Status */}
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-graphite">Account Status</label>
                  <div className="bg-silver-mist/50 p-4 border-l-4 border-zesty-lime rounded-lg">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleChange}
                        className="w-5 h-5 text-zesty-lime border-2 border-woodland-moss/30 focus:ring-zesty-lime focus:ring-2 focus:ring-offset-1"
                      />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-graphite">Active Account</span>
                        <span className="text-xs text-steel">
                          {formData.is_active 
                            ? 'User can log in and access assigned features' 
                            : 'User cannot log in or access the system'
                          }
                        </span>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="border-t border-silver-mist pt-8">
                <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                  <OmButton
                    onClick={onCancel}
                    disabled={isLoading}
                    buttonType="outlined-button"
                    buttonSize="medium"
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </OmButton>
                  <OmButton
                    type="submit"
                    disabled={isLoading}
                    buttonType="filled-button"
                    buttonSize="medium"
                    className="w-full sm:w-auto"
                  >
                    {isLoading ? (user ? "Updating User..." : "Creating User...") : (user ? 'Update User' : 'Create User')}
                  </OmButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserForm;
